import React, { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  CardActions,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

// Custom components and types
import { useParams } from "react-router-dom";
import { AlertsProps } from "components/Alerts/data/Alerts.type";
import { LocationConfiguration } from "pages/Settings/Configuration/data/LocationConfiguration";
import LoadingIndicator from "pages/Settings/LoadingIndicator";
import SnackbarAlert from "pages/Settings/SnackbarAlert";
import {
  _downloadMeasureTemplate,
  _uploadMeasure,
} from "middlewares/ReductionMeasure/ReductionMeasureApi";

interface UploadMeasureProps {
  closeDialog: () => void;
  getReductionMeasureListByLocationId(): void;
  showSnackbar?: (state: AlertsProps) => void;
}

interface UploadMeasureErrorItem {
  rowNumber: number;
  sheetName: string;
  field: string;
  column: string;
  code: string;
  message: string;
}

interface UploadMeasureResponse {
  locationId?: number;
  id?: number;
  success?: boolean;
  message?: string;
  userMessage?: string;
  errorCode?: string;
  totalRows?: number;
  failedRows?: number;
  created?: number;
  updated?: number;
  errors?: UploadMeasureErrorItem[];
}

const UploadMeasure: React.FC<UploadMeasureProps> = ({
  closeDialog,
  getReductionMeasureListByLocationId,
  showSnackbar,
}) => {
  const { LocationId } = useParams();
  const [locationConfig] = useState<LocationConfiguration>({
    id: 0,
    locationID: Number(LocationId),
    imageName: "",
    base64Image: "",
  });

  const [loading, setLoading] = useState(false);
  const [copyConfirmationOpen, setCopyConfirmationOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isSaveEnabled, setIsSaveEnabled] = useState(false);
  const [success, setSuccess] = useState<AlertsProps>({
    show: false,
    severity: "info",
    message: "",
  });
  const [uploadErrorDetails, setUploadErrorDetails] =
    useState<UploadMeasureResponse | null>(null);

  const getErrorMessage = (response?: UploadMeasureResponse | null) => {
    if (!response) {
      return "Failed to upload Excel. Please try again.";
    }

    return (
      response.userMessage ??
      response.message ??
      "Failed to upload Excel. Please try again."
    );
  };

  const handleUploadFailure = (response?: UploadMeasureResponse | null) => {
    if (response?.errorCode === "COPY_CONFIRMATION_REQUIRED") {
      setCopyConfirmationOpen(true);
      return;
    }

    const isValidationFailure =
      response?.errorCode === "UPLOAD_VALIDATION_FAILED" &&
      Array.isArray(response?.errors) &&
      (response?.errors?.length ?? 0) > 0;

    setUploadErrorDetails(isValidationFailure ? response ?? null : null);
    notifySnackBar({
      show: true,
      severity: "error",
      message: getErrorMessage(response),
    });
  };

  const parseUploadError = (err: unknown): UploadMeasureResponse | null => {
    if (typeof err === "string") {
      return { message: err };
    }

    if (!err || typeof err !== "object") {
      return null;
    }

    // apiHandler wraps Axios errors into ApiError — response body is in err.data, not err.response.data
    const possibleAxiosShape = err as { response?: { data?: unknown }; data?: unknown };
    const payload = possibleAxiosShape.response?.data ?? possibleAxiosShape.data ?? err;

    if (typeof payload === "string") {
      return { message: payload };
    }

    if (payload && typeof payload === "object") {
      return payload as UploadMeasureResponse;
    }

    return null;
  };

  /**
   * Handles the file drop event from the dropzone.
   * Only stores the file for upload, no parsing needed.
   */
  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setSelectedFile(acceptedFiles[0]);
      setUploadErrorDetails(null);
      setCopyConfirmationOpen(false);
      setIsSaveEnabled(true); // Enable Save after selecting
    }
  }, []);

  /** Handles closing of the snackbar. */
  const handleCloseSnackBar = (state: AlertsProps) => {
    setSuccess(state);
  };

  const notifySnackBar = (state: AlertsProps) => {
    if (showSnackbar) {
      showSnackbar(state);
      return;
    }

    handleCloseSnackBar(state);
  };

  /** Handles saving (upload to backend). */
  const handleSave = async (copyMeasures = false) => {
    if (!selectedFile || loading) return;
    try {
      setLoading(true);
      setCopyConfirmationOpen(false);
      setUploadErrorDetails(null);
      const formData = new FormData();
      formData.append("locationID", String(locationConfig.locationID));
      formData.append("fileName", selectedFile.name);
      formData.append("file", selectedFile, selectedFile.name);
      formData.append("id", String(locationConfig.id));
      formData.append("copyMeasures", String(copyMeasures));

      const res = (await _uploadMeasure(formData)) as
        | UploadMeasureResponse
        | boolean
        | null;

      if (res && typeof res === "object" && res.success === false) {
        handleUploadFailure(res);
        return;
      }

      if (res) {
        notifySnackBar({
          show: true,
          severity: "success",
          message:
            (typeof res === "object" &&
              (res.userMessage ?? res.message)) ||
            "Excel file uploaded successfully!",
        });
        closeDialog();
        getReductionMeasureListByLocationId();
      } else {
        handleUploadFailure(null);
      }
    } catch (err) {
      const uploadError = parseUploadError(err);
      handleUploadFailure(uploadError);
    } finally {
      setLoading(false);
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "application/vnd.ms-excel.sheet.macroEnabled.12": [".xlsm"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.template": [
        ".xltx",
      ],
      "application/vnd.ms-excel.template.macroEnabled.12": [".xltm"],
    },
    maxSize: 10 * 1024 * 1024, // 10MB
    multiple: false,
    disabled: loading || copyConfirmationOpen,
  });

  const handleDownloadTemplate = async () => {
    try {
      const res = await _downloadMeasureTemplate();
      const url = window.URL.createObjectURL(new Blob([res]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "ReductionMeasuresTemplate.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Download failed:", err);
    }
  };

  return (
    <>
      <Typography variant="h4" gutterBottom textAlign="center">
        Bulk Upload Reduction Measures
      </Typography>
      <Typography variant="body1" textAlign="center" sx={{ mb: 3 }}>
        Upload an Excel (.xlsx, .xlsm, .xltx, or .xltm) file to add multiple reduction measures
        at once. Please ensure your file follows the required template format to
        avoid errors.
      </Typography>
      <Box textAlign="center" sx={{ mb: 2 }}>
        <Button variant="outlined" onClick={handleDownloadTemplate}>
          Download Excel Template
        </Button>
      </Box>
      <Box sx={{ maxWidth: 600, margin: "auto", mt: 4 }}>
        <Card elevation={3}>
          <CardContent>
            {selectedFile && (
              <>
                <Typography variant="body2" sx={{ fontWeight: "bold", mb: 2 }}>
                  <b>Selected File:</b> {selectedFile.name}
                </Typography>
                <Divider sx={{ my: 2 }} />
              </>
            )}

            <Box
              {...getRootProps()}
              sx={{
                border: "2px dashed #ccc",
                borderRadius: 2,
                padding: 3,
                textAlign: "center",
                cursor: "pointer",
                backgroundColor: "#f9f9f9",
                "&:hover": { backgroundColor: "#f1f1f1" },
              }}
              data-testid="dropzone"
            >
              <input {...getInputProps()} />
              <CloudUploadIcon sx={{ fontSize: 40, color: "orange" }} />
              <Typography variant="body1" sx={{ mt: 1 }}>
                {selectedFile
                  ? selectedFile.name
                  : "Drag and drop your Excel file here, or click Browse Excel to select a file."}
              </Typography>
              <Button
                variant="outlined"
                color="warning"
                sx={{ mt: 2, textTransform: "none" }}
              >
                Browse Excel
              </Button>
              <Typography
                variant="caption"
                display="block"
                sx={{ mt: 1, color: "gray" }}
              >
                Accepted formats: .xlsx, .xlsm, .xltx, .xltm | Max size: 10 MB
              </Typography>
            </Box>

            {uploadErrorDetails?.errors && uploadErrorDetails.errors.length > 0 && (
              <Box
                sx={{ mt: 2, p: 2, borderRadius: 1, backgroundColor: "#fff4f4" }}
                data-testid="upload-validation-errors"
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Upload failed!!
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {(uploadErrorDetails.failedRows ?? uploadErrorDetails.errors.length)} of {uploadErrorDetails.totalRows ?? uploadErrorDetails.errors.length} rows contain errors.
                </Typography>

                <List dense sx={{ mt: 1, mb: 0 }}>
                  {uploadErrorDetails.errors.slice(0, 5).map((error, index) => (
                    <ListItem key={`${error.rowNumber}-${error.field}-${index}`} sx={{ py: 0.25 }}>
                      <ListItemText
                        primary={`Row ${error.rowNumber}: ${error.message}`}
                      />
                    </ListItem>
                  ))}
                </List>

                {uploadErrorDetails.errors.length > 5 && (
                  (() => {
                    const remaining = uploadErrorDetails.errors.length - 5;
                    const remainingLabel = remaining === 1 ? "error" : "errors";
                    return (
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    ...and {remaining} more {remainingLabel}
                  </Typography>
                    );
                  })()
                )}
              </Box>
            )}
          </CardContent>

          <CardActions sx={{ justifyContent: "center" }}>
            <Button
              variant="contained"
              color="primary"
              sx={{ mt: 2 }}
              disabled={!isSaveEnabled || loading || copyConfirmationOpen}
              data-testid="saveButton"
              onClick={() => handleSave()}
            >
              Upload
            </Button>
          </CardActions>
        </Card>
      </Box>

      <Dialog
        open={copyConfirmationOpen}
        onClose={() => setCopyConfirmationOpen(false)}
        aria-labelledby="copy-measures-title"
        aria-describedby="copy-measures-description"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="copy-measures-title">
          Copy measures from another location?
        </DialogTitle>
        <DialogContent id="copy-measures-description">
          <DialogContentText>
            This file contains reduction measures that already belong to another
            location. Would you like to copy them to this location?
          </DialogContentText>
          <DialogContentText sx={{ mt: 2 }}>
            New copies will be created with new Measure IDs. The original measures
            will remain unchanged.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCopyConfirmationOpen(false)} autoFocus>
            Cancel
          </Button>
          <Button variant="contained" onClick={() => handleSave(true)}>
            Copy measures
          </Button>
        </DialogActions>
      </Dialog>
      <LoadingIndicator loading={loading} />
      {!showSnackbar && (
        <SnackbarAlert
          success={success}
          handleCloseSnackBar={handleCloseSnackBar}
        />
      )}
    </>
  );
};

export default UploadMeasure;
